import * as THREE from "three";
import { NavigationControls } from "./navigation_controls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { getExperimentalPreferenceDefaults } from "../ui/experimental_persistence.js";
import { getBuiltinEnvironment, getViewerToneMapping, applyModelEnvironmentIntensity } from "../logic/viewer_lighting.js";
import { prepareInnoWorksConfigurables, updateInnoWorksAnimations, updateInnoWorksAutoPlay, findRootNodeIndex } from "../logic/modelvista_runtime.js";
import { resolveModelVistaNodePath } from "../logic/modelvista_extension.js";
import { createModelVistaBindings } from "../logic/modelvista_bindings.js";
import { applyAwakeCameraStates } from "../logic/camera_reference.js";
import { readGlb } from "./glb.js";
import { modelBounds, centerModelOrbit } from "./framing.js";
import { paintValues, applyPaint, assignedMaterialReference } from "./materials.js";
import { upgradeModelVistaPhysicalMaterial } from "../logic/modelvista_clearcoat.js";
import { materialAssignments, assignMaterial } from "../logic/material_slots.js";
import { captureCameraView, restoreCameraView } from "./camera_view.js";
import { readObjectTransforms } from "./object_properties.js";
import { bindPartSelection } from "./part_selection.js";

const vectorData = v => ({ X: v.x, Y: v.y, Z: v.z });
const quaternionData = q => ({ X: q.x, Y: q.y, Z: q.z, W: q.w });

export function createViewport(canvas, { onSelect, onEdit, onSelectionChanged, onLightingChanged, onCameraChanged, onCameraEdit, onObjectPropertiesChanged } = {}) {
    let materialPreview = null;
    function cancelMaterialPreview() {
        if (!materialPreview) return;
        materialPreview.assignments.forEach(assignment => assignMaterial(assignment, assignment.material));
        materialPreview.material.dispose(); materialPreview = null;
    }
    async function beginMaterialPreview(reference, targets) {
        cancelMaterialPreview();
        let source;
        if (Number.isInteger(reference?.MaterialId)) source = state.innoWorksMaterialsById?.get(reference.MaterialId);
        else if (reference) {
            const index = state.gltf.materials?.findIndex(material => material.name === reference.MaterialName);
            if (index >= 0) source = state.materialsByIndex?.get(index) ?? await model.parser.getDependency("material", index);
        }
        if (reference && !source) throw new Error("Choose a material first.");
        if (source && !source.isMeshStandardMaterial) throw new Error("Unlit materials do not support the paint editor.");
        const material = source?.isMeshPhysicalMaterial ? source.clone() : upgradeModelVistaPhysicalMaterial(source);
        const assignments = targets.flatMap(target => materialAssignments(objectForPath(target.Path), target.MaterialSlot, state.primitiveIndexByMesh));
        if (!assignments.length) { material.dispose(); throw new Error("Add a material target before editing."); }
        materialPreview = { material, assignments };
        assignments.forEach(assignment => assignMaterial(assignment, material));
        const maps = [["Base color", material.map], ["Normal", material.normalMap], ["Metallic", material.metalnessMap],
            ["Roughness", material.roughnessMap], ["Occlusion", material.aoMap], ["Emission", material.emissiveMap],
            ["Clearcoat", material.clearcoatMap], ["Clearcoat roughness", material.clearcoatRoughnessMap], ["Clearcoat normal", material.clearcoatNormalMap]]
            .filter(([, texture]) => texture).map(([label]) => label);
        return { ...paintValues(material), textured: !!material.map, maps };
    }
    function previewMaterial(values) { if (materialPreview) applyPaint(materialPreview.material, values); }
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Start Create with Cannon Exterior and default lighting, independent of saved Viewer settings.
    const preferences = getExperimentalPreferenceDefaults();
    const toneMapping = getViewerToneMapping(preferences.toneMap);
    renderer.toneMapping = toneMapping.mode;
    renderer.toneMappingExposure = toneMapping.exposureBoost / Math.pow(2, preferences.exposureSetting);
    const scene = new THREE.Scene(); scene.background = new THREE.Color("#10151e");
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const environmentChoice = getBuiltinEnvironment(preferences.selectedEnvironment);
    let environment = null, environmentLoading = null;
    const sun = new THREE.DirectionalLight(0xffffff, 1);
    const ambient = new THREE.AmbientLight(0xffffff, 0.25);
    const azimuth = THREE.MathUtils.degToRad(preferences.lightDirectionDegrees);
    const elevation = THREE.MathUtils.degToRad(preferences.lightHeight);
    const radius = Math.sqrt(5 * 5 + 10 * 10 + 7 * 7);
    sun.position.set(Math.sin(azimuth) * Math.cos(elevation) * radius, Math.sin(elevation) * radius, Math.cos(azimuth) * Math.cos(elevation) * radius);
    sun.visible = ambient.visible = preferences.punctualLights;
    scene.add(sun, ambient);
    async function ensureEnvironment() {
        if (environment) return;
        if (environmentLoading) return environmentLoading;
        environmentLoading = (async () => {
            let texture;
            try {
                const url = new URL(`../assets/environments/${environmentChoice.id}.hdr`, location.href);
                texture = await new RGBELoader().loadAsync(url.href);
                if (disposed) throw new Error("The authoring view was closed.");
                texture.mapping = THREE.EquirectangularReflectionMapping;
                environment = pmrem.fromEquirectangular(texture);
                scene.environment = preferences.ibl ? environment.texture : null;
                scene.background = preferences.renderEnv ? environment.texture : new THREE.Color(preferences.clearColor);
                scene.backgroundBlurriness = preferences.blurEnvAmount / 100;
                if (scene.environmentIntensity !== undefined) scene.environmentIntensity = preferences.ibl ? Math.pow(10, preferences.iblIntensity) : 0;
                const rotation = THREE.MathUtils.degToRad({ "+X": 0, "+Z": 90, "-X": 180, "-Z": 270 }[preferences.selectedEnvironmentRotation] ?? 0);
                scene.environmentRotation?.set(0, rotation, 0);
                scene.backgroundRotation?.set(0, rotation, 0);
                onLightingChanged?.(`${environmentChoice.name}${environmentChoice.fallback ? " (fallback)" : ""}${preferences.ibl ? "" : " · IBL off"}`);
            } catch (error) {
                throw new Error(`Could not load the ${environmentChoice.name} HDR. Check your connection and try importing again. ${error.message}`);
            } finally { texture?.dispose(); environmentLoading = null; }
        })();
        return environmentLoading;
    }
    const cameraPerspective = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
    const cameraOrthographic = new THREE.OrthographicCamera(-2, 2, 2, -2, 0.01, 1000);
    let camera = cameraPerspective, state = null, model = null, selectedIndices = [], mode = "inspect", preview = false;
    camera.position.set(3, 2, 4);
    const controls = new NavigationControls(camera, canvas);
    const gizmo = new TransformControls(camera, canvas); gizmo.setSpace("local"); gizmo.enabled = false; scene.add(gizmo);
    const outline = new THREE.Box3Helper(new THREE.Box3(), 0x6ea9ff); outline.visible = false; scene.add(outline);
    const grid = new THREE.GridHelper(10, 20, 0x394658, 0x243041); scene.add(grid);
    let disposed = false;

    function syncCameraState() {
        if (!state) return;
        state.cameraState.target.copy(controls.target);
        state.cameraState.distance = camera.position.distanceTo(controls.target);
    }
    function captureView() { return captureCameraView(cameraPerspective, cameraOrthographic, camera, controls, state?.cameraState); }
    function restoreView(view) {
        camera = restoreCameraView(view, cameraPerspective, cameraOrthographic, controls, state);
        gizmo.camera = camera;
        resize();
        onCameraChanged?.();
    }
    controls.addEventListener("change", syncCameraState);
    controls.addEventListener("start", () => {
        if (state) state.innoWorksActiveAnimations = state.innoWorksActiveAnimations.filter(animation => animation.type !== "camera");
        onCameraEdit?.();
    });
    gizmo.addEventListener("dragging-changed", event => { controls.enabled = !event.value; });
    gizmo.addEventListener("objectChange", () => { publishObjectProperties(); onEdit?.(); });
    function resize() {
        const width = Math.max(canvas.clientWidth, 1), height = Math.max(canvas.clientHeight, 1);
        renderer.setSize(width, height, false);
        cameraPerspective.aspect = width / height; cameraPerspective.updateProjectionMatrix();
        updateOrtho();
    }
    function updateOrtho() {
        const size = state?.cameraState.orthographicSize ?? 2;
        const aspect = Math.max(canvas.clientWidth, 1) / Math.max(canvas.clientHeight, 1);
        cameraOrthographic.left = -size * aspect; cameraOrthographic.right = size * aspect;
        cameraOrthographic.top = size; cameraOrthographic.bottom = -size; cameraOrthographic.updateProjectionMatrix();
    }
    function applyProjection(projection) {
        const next = projection.projectionMode === "orthographic" ? cameraOrthographic
            : projection.projectionMode === "perspective" ? cameraPerspective : camera;
        if (next !== camera) { next.position.copy(camera.position); next.quaternion.copy(camera.quaternion); camera = next; controls.object = camera; gizmo.camera = camera; }
        if (Number.isFinite(projection.fieldOfViewRadians)) cameraPerspective.fov = THREE.MathUtils.radToDeg(projection.fieldOfViewRadians);
        if (Number.isFinite(projection.orthographicSize)) {
            state.cameraState.orthographicSize = projection.orthographicSize;
            cameraOrthographic.zoom = 1;
        }
        if (state) state.userCamera = camera;
        cameraPerspective.updateProjectionMatrix(); updateOrtho();
    }
    function frame(object = model?.scene, preserveDirection = false) {
        if (!object) return;
        const box = modelBounds(object);
        if (box.isEmpty()) return;
        const sphere = box.getBoundingSphere(new THREE.Sphere());
        const radius = Math.max(sphere.radius, 0.01);
        const distance = radius / Math.sin(THREE.MathUtils.degToRad(cameraPerspective.fov / 2)) * 1.25;
        cameraPerspective.near = cameraOrthographic.near = Math.max(radius / 1000, 0.00001);
        cameraPerspective.far = cameraOrthographic.far = radius * 10000;
        const direction = preserveDirection ? camera.position.clone().sub(controls.target) : new THREE.Vector3(1, 0.7, 1.2);
        if (direction.lengthSq() === 0) direction.set(1, 0.7, 1.2);
        controls.target.copy(sphere.center);
        camera.position.copy(sphere.center).add(direction.normalize().multiplyScalar(distance));
        camera.lookAt(sphere.center);
        if (state) state.cameraState.orthographicSize = radius * 1.2;
        grid.position.y = box.min.y - radius * 0.003;
        grid.scale.setScalar(radius / 2);
        resize(); controls.update(); syncCameraState();
    }
    function disposeModel(gltf, modelState) {
        if (!gltf) return;
        const geometry = new Set(), materials = new Set(), textures = new Set();
        gltf.scene.traverse(object => { if (object.geometry) geometry.add(object.geometry); for (const material of Array.isArray(object.material) ? object.material : [object.material]) if (material) materials.add(material); });
        for (const map of [modelState?.materialsByIndex, modelState?.innoWorksMaterialsById]) map?.forEach(material => materials.add(material));
        materials.forEach(material => { Object.values(material).forEach(value => { if (value?.isTexture) textures.add(value); }); material.dispose(); });
        textures.forEach(texture => texture.dispose()); geometry.forEach(value => value.dispose());
        modelState?.innoWorksObjectUrls?.forEach(url => URL.revokeObjectURL(url));
        modelState?.modelEntry?.mixer?.stopAllAction();
    }
    async function load(bytes, { reframe = false, selections = {}, applyInitialCamera = false, deferredStateIndices = [] } = {}) {
        const source = readGlb(bytes);
        await ensureEnvironment();
        const loader = new GLTFLoader(); const draco = new DRACOLoader();
        draco.setDecoderPath(new URL("../libs/draco/", location.href).href); loader.setDRACOLoader(draco);
        let next, nextState;
        try {
            next = await loader.parseAsync(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength), "");
            const sceneIndex = source.json.scene ?? 0;
            nextState = { gltf: next.parser.json, gltfJson: next.parser.json, sceneIndex,
                authoringDeferredStateIndices: deferredStateIndices,
                innoWorksPackage: { ...source, extension: source.json.extensions?.INNOWORKS_configurables },
                userCamera: camera, cameraState: { target: controls.target.clone(), distance: camera.position.distanceTo(controls.target), orthographicSize: state?.cameraState.orthographicSize ?? 2 },
                isSelected: true, selectedConfigurableOptions: {}, innoWorksActiveAnimations: [],
                innoWorksUiModel: { updateConfigurableSelection: (key, index) => onSelectionChanged?.(key, index) },
                applyUserCameraProjection: applyProjection,
                ...await createModelVistaBindings(next, next.scene, sceneIndex) };
            nextState.modelEntry = { object: next.scene, activeAnimationIndices: [], actions: [], mixer: null };
            if (next.animations.length) {
                nextState.modelEntry.mixer = new THREE.AnimationMixer(next.scene);
                nextState.modelEntry.actions = next.animations.map(clip => nextState.modelEntry.mixer.clipAction(clip));
            }
            // The runtime can change projection during initialization. Restore
            // the previous state if staging fails, without losing the model.
            const previous = state;
            state = nextState;
            try { await prepareInnoWorksConfigurables(nextState, source.json.extensions?.INNOWORKS_configurables); }
            catch (error) { state = previous; throw error; }
        } catch (error) { disposeModel(next, nextState); throw error; }
        finally { draco.dispose(); }
        const previousModel = model;
        const previousState = model?._modelVistaState;
        gizmo.detach(); if (model) scene.remove(model.scene);
        model = next; state = nextState; model._modelVistaState = state; scene.add(model.scene);
        applyModelEnvironmentIntensity(model.scene, state, preferences);
        if (!preferences.punctualLights) model.scene.traverse(node => { if (node.isLight) node.visible = false; });
        disposeModel(previousModel, previousState);
        if (reframe) frame();
        if (applyInitialCamera) applyAwakeCameraStates(state.innoWorksRuntime);
        // Restore composite choices first, then any individual overrides.
        for (const [containerIndex, optionIndex] of Object.entries(selections).sort(([a], [b]) => Number(entryAt(Number(b))?.type === "Group") - Number(entryAt(Number(a))?.type === "Group"))) apply(Number(containerIndex), optionIndex, true);
        controls.target.copy(state.cameraState.target); controls.update();
        if (reframe || applyInitialCamera) recenterOrbit();
        select(selectedIndices); return state;
    }
    function entryAt(index) { return [...(state?.innoWorksRuntime?.values() ?? [])].find(entry => Number(entry.id.split("|").at(-2)) === index); }
    function apply(index, option, immediate = false) {
        const entry = entryAt(index);
        if (!entry) return false;
        syncCameraState(); entry.applyOption(option, { forceImmediate: immediate });
        controls.target.copy(state.cameraState.target); controls.update(); return true;
    }
    function select(indices) {
        selectedIndices = [...indices]; gizmo.detach(); outline.visible = false;
        publishObjectProperties();
        if (preview || !state) return;
        const object = state.nodeIndexToObject.get(indices[0]);
        if (!object) return;
        outline.box.makeEmpty();
        indices.forEach(index => { const target = state.nodeIndexToObject.get(index); if (target) outline.box.expandByObject(target); });
        outline.visible = !outline.box.isEmpty();
        if (mode !== "inspect" && indices.length === 1) { gizmo.setMode(mode); gizmo.enabled = true; gizmo.attach(object); }
    }
    function publishObjectProperties() {
        if (onObjectPropertiesChanged) onObjectPropertiesChanged(readObjectTransforms(state?.nodeIndexToObject, selectedIndices));
    }
    function setTool(value) { mode = value; gizmo.enabled = value !== "inspect"; select(selectedIndices); }
    function recenterOrbit() {
        if (!model || !state) return;
        state.innoWorksActiveAnimations = state.innoWorksActiveAnimations.filter(animation => animation.type !== "camera");
        if (centerModelOrbit(model.scene, camera, controls)) syncCameraState();
    }
    function setPreview(value) { preview = value; if (value) { setTool("inspect"); recenterOrbit(); } select(selectedIndices); }
    function objectForPath(path) {
        const root = findRootNodeIndex(state.gltf, state.innoWorksPackage.extension.RootName, state.sceneIndex);
        const node = resolveModelVistaNodePath(state.gltf, root, path);
        return state.nodeIndexToObject.get(node);
    }
    function captureTransform(path) {
        const object = objectForPath(path);
        if (!object) throw new Error("The transform target could not be found.");
        return { LocalPosition: vectorData(object.position), LocalRotation: quaternionData(object.quaternion) };
    }
    function captureHierarchyTransforms() {
        const matrices = new Map();
        for (const [index, object] of state?.nodeIndexToObject ?? []) {
            object.updateWorldMatrix(true, false); matrices.set(index, object.matrixWorld.toArray());
        }
        return matrices;
    }
    function captureHierarchyParts() {
        return new Map([...(state?.nodeIndexToObject ?? [])].map(([index, object]) => [index, {
            translation: object.position.toArray(), rotation: object.quaternion.toArray(), scale: object.scale.toArray(), visible: object.visible
        }]));
    }
    function captureMaterial(path, slot = 0) { return assignedMaterialReference(state, objectForPath(path), slot); }
    function captureCamera(referencePath = "") {
        const root = objectForPath(referencePath);
        if (!root) throw new Error("The camera reference could not be found.");
        root.updateWorldMatrix(true, false); camera.updateMatrixWorld();
        // Unity's exported camera options face +Z; Three cameras face -Z.
        const local = root.matrixWorld.clone().invert().multiply(camera.matrixWorld).multiply(new THREE.Matrix4().makeRotationY(Math.PI));
        const position = new THREE.Vector3(), rotation = new THREE.Quaternion(), scale = new THREE.Vector3();
        local.decompose(position, rotation, scale);
        return { LocalPosition: vectorData(position), LocalRotation: quaternionData(rotation), FieldOfView: cameraPerspective.fov,
            Orthographic: camera === cameraOrthographic, OrthographicSize: state.cameraState.orthographicSize / cameraOrthographic.zoom,
            ApplyOrthographicSize: true };
    }
    function setCameraLens({ fieldOfView, orthographic, orthographicSize }) {
        if (!state) return;
        if (fieldOfView !== undefined && (!Number.isFinite(fieldOfView) || fieldOfView < 1 || fieldOfView > 179)) throw new Error("Field of view must be between 1 and 179 degrees.");
        if (orthographicSize !== undefined && (!Number.isFinite(orthographicSize) || orthographicSize < 0.001)) throw new Error("Orthographic half-height must be at least 0.001 m.");
        // A manual lens adjustment takes over from an in-progress Camera State.
        state.innoWorksActiveAnimations = state.innoWorksActiveAnimations.filter(animation => animation.type !== "camera");
        applyProjection({
            projectionMode: orthographic === undefined ? undefined : orthographic ? "orthographic" : "perspective",
            fieldOfViewRadians: fieldOfView === undefined ? undefined : THREE.MathUtils.degToRad(fieldOfView),
            orthographicSize
        });
        onCameraChanged?.();
    }
    function thumbnail() {
        const oldOutline = outline.visible, oldGizmo = gizmo.visible;
        try {
            outline.visible = false; gizmo.visible = false; renderer.render(scene, camera);
            const image = document.createElement("canvas"); image.width = 256; image.height = 192;
            const context = image.getContext("2d");
            const scale = Math.min(256 / canvas.width, 192 / canvas.height);
            const width = canvas.width * scale, height = canvas.height * scale;
            context.fillStyle = "#10151e"; context.fillRect(0, 0, 256, 192);
            context.drawImage(canvas, (256 - width) / 2, (192 - height) / 2, width, height);
            return image.toDataURL("image/png");
        } finally { outline.visible = oldOutline; gizmo.visible = oldGizmo; }
    }
    const disposePartSelection = bindPartSelection(canvas, { getScene: () => model?.scene, getCamera: () => camera,
        getNodes: () => state?.nodeIndexToObject, isEnabled: () => !preview, isTransforming: () => gizmo.dragging, onSelect });
    const observer = new ResizeObserver(resize); observer.observe(canvas); resize();
    const clock = new THREE.Clock();
    let readoutElapsed = 0;
    function render() {
        if (disposed) return;
        const delta = Math.min(clock.getDelta(), 0.1);
        readoutElapsed += delta;
        if (state) {
            if (preview) updateInnoWorksAutoPlay(state, delta);
            if (!state.animationPlaybackPaused) state.modelEntry.mixer?.update(delta);
            const cameraAnimating = state.innoWorksActiveAnimations.some(animation => animation.type === "camera");
            updateInnoWorksAnimations(state, delta);
            if (cameraAnimating) { controls.target.copy(state.cameraState.target); controls.update(); }
            if (outline.visible) {
                outline.box.makeEmpty();
                selectedIndices.forEach(index => { const target = state.nodeIndexToObject.get(index); if (target) outline.box.expandByObject(target); });
            }
        }
        if (state && readoutElapsed >= 0.1) {
            readoutElapsed = 0; onCameraChanged?.();
            if (!preview && selectedIndices.length) publishObjectProperties();
        }
        renderer.render(scene, camera); requestAnimationFrame(render);
    }
    render();
    return { load, apply, frame: () => frame(), frameSelected: () => frame(selectedIndices.map(index => state?.nodeIndexToObject.get(index)), true), select, setTool, setPreview,
        captureTransform, captureHierarchyTransforms, captureHierarchyParts, captureMaterial, captureCamera, captureView, restoreView, setCameraLens, thumbnail, getState: () => state,
        beginMaterialPreview, previewMaterial, cancelMaterialPreview,
        dispose() { disposed = true; disposePartSelection(); cancelMaterialPreview(); observer.disconnect(); disposeModel(model, state); controls.dispose(); gizmo.dispose(); environment?.dispose(); pmrem.dispose(); renderer.dispose(); } };
}
